// ViewDocReplicaView — spec decision 3: a form-style request "renders a
// read-only REPLICA of the recipient's own page, populated with whatever they
// have filled so far".
//
// Two properties this surface has to hold, because they are the reason the
// replica exists rather than a table of answers:
//
//   1. An UNANSWERED choice still shows every option the recipient sees. An
//      operator looking at a half-filled form needs to know what was asked, not
//      just what came back.
//   2. An unanswered field is VISIBLY unanswered — a dashed well, never a gap.
//      A blank line reads as "no", and "no" is a different fact from "not yet".
//
// The section order and the field order mirror the recipient's page exactly,
// because the operator is usually on the phone with them saying "the third
// question down". Sections are rendered in order and never merged by heading:
// each tenant-intake animal is its OWN section ("Your animals · Rex"),
// mirroring the per-animal card the applicant actually fills in.
//
// Nothing here writes. Recording answers on someone's behalf belongs to the
// DOCUMENT, not to a section inside it (the document kind already names the
// section), so that affordance lives in ViewDocViewer.

import { StatusChip } from '@/components/oc/bits';
import { SignatureBlock } from './ViewDocPanelView';
import type {
  ReplicaSectionStatus,
  ViewDocReplica,
  ViewDocReplicaField,
} from '@/lib/viewDocsClient';

const SECTION_STATUS_LABEL: Record<ReplicaSectionStatus, string> = {
  not_requested: 'Not requested',
  requested: 'Waiting on them',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const SECTION_STATUS_TONE: Record<
  ReplicaSectionStatus,
  'done' | 'blocked' | 'closed' | 'active' | 'active-soft'
> = {
  not_requested: 'active-soft',
  requested: 'active',
  completed: 'done',
  cancelled: 'closed',
};

function hasAnswer(field: ViewDocReplicaField): boolean {
  if (Array.isArray(field.value)) return field.value.length > 0;
  return typeof field.value === 'string' && field.value.trim().length > 0;
}

function Unanswered({ what }: { what: string }): React.JSX.Element {
  return <span className="vd-unanswered">{what}</span>;
}

function FieldValue({ field }: { field: ViewDocReplicaField }): React.JSX.Element {
  const answered = hasAnswer(field);

  if (field.kind === 'choice') {
    const options = field.options ?? [];
    const chosen = Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];
    if (options.length === 0) {
      return answered ? (
        <span className="vd-field-value">{chosen.join(', ')}</span>
      ) : (
        <Unanswered what="Not answered" />
      );
    }
    return (
      <span className="vd-choices">
        {options.map((option) => (
          <span
            key={option}
            className="vd-choice-opt"
            data-selected={chosen.includes(option) ? 'true' : undefined}
          >
            {option}
          </span>
        ))}
        {!answered && <Unanswered what="Not answered" />}
      </span>
    );
  }

  if (!answered) {
    return (
      <Unanswered
        what={field.kind === 'list' ? 'Nothing listed yet' : 'Not answered'}
      />
    );
  }

  if (field.kind === 'list') {
    const items = Array.isArray(field.value) ? field.value : [field.value as string];
    return (
      <ul className="vd-list">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }

  const text = Array.isArray(field.value) ? field.value.join('\n') : (field.value ?? '');
  if (field.kind === 'signature') return <SignatureBlock name={text} signedAt={field.signedAt} />;
  if (field.kind === 'address') return <span className="vd-address">{text}</span>;
  return <span className="vd-field-value">{text}</span>;
}

export function ViewDocReplicaView({
  replica,
}: {
  replica: ViewDocReplica;
}): React.JSX.Element {
  if (replica.sections.length === 0) {
    return (
      <div className="oc-empty">
        This request has no sections yet — nothing has been asked of them.
      </div>
    );
  }

  return (
    <div className="vd-replica">
      {replica.sections.map((section, si) => (
        <section
          key={`${section.heading}-${si}`}
          className="vd-replica-section"
          data-status={section.status}
        >
          <div className="vd-replica-head">
            <span className="vd-replica-title">{section.heading}</span>
            <StatusChip tone={SECTION_STATUS_TONE[section.status]}>
              {SECTION_STATUS_LABEL[section.status]}
            </StatusChip>
          </div>

          {section.fields.length === 0 ? (
            <div className="oc-meta">No questions in this section.</div>
          ) : (
            <div className="vd-fields">
              {section.fields.map((field, fi) => (
                <div key={`${field.label}-${fi}`}>
                  <span className="vd-field-label">{field.label}</span>
                  <FieldValue field={field} />
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
